import get from 'lodash/get'

import { ConverterRule } from 'common/builder/components/chart/FlowChart/lib/types'
import errorTrackingService from 'utils/services/errorTrackingService'

export class PredicateConverter {
  private reportNoConversionRuleError = (entity: unknown) => {
    errorTrackingService.trackError('Unable to convert entity: No conversion rule', {
      extra: {
        entity,
      },
    })
  }

  private reportConvesionError = (error: unknown, entity: unknown) => {
    errorTrackingService.trackError('Unable to convert entity: Conversion failed', {
      extra: {
        error: get(error, 'message'),
        entity,
      },
    })
  }

  private reportPredicateError = (error: unknown, entity: unknown) => {
    errorTrackingService.trackError('Unable to convert entity: Predicate failed', {
      extra: {
        error: get(error, 'message'),
        entity,
      },
    })
  }

  private convertEntity<InputEntity, OutputEntity, Payload = object>({
    entity,
    rules,
    payload,
  }: {
    entity: InputEntity
    rules: Array<ConverterRule<InputEntity, OutputEntity, Payload>>
    payload?: Payload
  }) {
    let convertRule
    let hasPredicateError = false

    for (const rule of rules) {
      try {
        if (rule.predicate(entity)) {
          convertRule = rule
          break
        }
      } catch (error) {
        this.reportPredicateError(error, entity)
        hasPredicateError = true
      }
    }

    if (!convertRule && !hasPredicateError) {
      this.reportNoConversionRuleError(entity)
      return null
    }

    if (!convertRule) {
      return null
    }

    let result = null

    try {
      result = convertRule.convert(entity, payload || ({} as Payload))
    } catch (error) {
      this.reportConvesionError(error, entity)
    }

    return result
  }

  convertEntityMap<InputEntity, OutputEntity, Payload = object>({
    entities,
    rules,
    payload,
  }: {
    entities: Record<string, InputEntity>
    rules: Array<ConverterRule<InputEntity, OutputEntity, Payload>>
    payload?: Payload
  }): Record<string, OutputEntity> {
    return Object.keys(entities).reduce((acc: Record<string, OutputEntity>, key) => {
      const converted = this.convertEntity({ entity: entities[key], rules, payload })

      if (converted) {
        acc[key] = converted
      }

      return acc
    }, {})
  }

  convertEntities<InputEntity, OutputEntity, Payload = object>({
    entities,
    rules,
    payload,
  }: {
    entities: InputEntity[]
    rules: Array<ConverterRule<InputEntity, OutputEntity, Payload>>
    payload?: Payload
  }): OutputEntity[] {
    return entities.reduce((acc: OutputEntity[], entity) => {
      const convertedEntity = this.convertEntity({ entity, rules, payload })
      if (convertedEntity) {
        acc.push(convertedEntity)
      }

      return acc
    }, [])
  }
}

export const predicateConverter = new PredicateConverter()
